import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PlusIcon, ServerIcon, CpuChipIcon, CircleStackIcon, BuildingOfficeIcon, ChevronRightIcon, ChevronDownIcon, FolderIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { Project, ProjectCreate, Environment, Deployment, Region } from '../types/api';
import Logger from '../utils/logger';
import { useOrganisation } from '../contexts/OrganisationContext';
import { useProjectTree } from '../contexts/ProjectTreeContext';
import ProjectAccessBadge from '../components/ProjectAccessBadge';
import { SuccessMessages, ErrorMessages, getApiErrorMessage } from '../constants/messages';
import { Button } from '../components/ui';
import { useTranslation } from 'react-i18next';

interface InstanceType {
  id: string;
  name: string;
  slug: string;
  cpu: number;
  ram_gb: number;
  description?: string;
  active: boolean;
}

interface ProjectForm {
  nom: string;
  description?: string;
  resource_mode: 'PRESET' | 'CUSTOM';
  instance_type_id?: string;
  quota_cpu?: number;
  quota_ram?: number;
  quota_stockage: number;
  region_id?: string;
}

const ProjectsPage: React.FC = () => {
  const { t } = (useTranslation as any)('projects');
  const { currentOrganisation, refreshQuotaUsage, selectedRegion } = useOrganisation();
  const { expandedProjectId, setExpandedProjectId } = useProjectTree();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [instanceTypes, setInstanceTypes] = useState<InstanceType[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [resourceMode, setResourceMode] = useState<'PRESET' | 'CUSTOM'>('PRESET');

  // Expandable rows state (expandedProjectId from shared context)
  const [projectEnvironments, setProjectEnvironments] = useState<Record<string, Environment[]>>({});
  const [projectDeployments, setProjectDeployments] = useState<Record<string, Deployment[]>>({});
  const [loadingEnvs, setLoadingEnvs] = useState<string | null>(null);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<ProjectForm>({
    defaultValues: {
      resource_mode: 'PRESET',
      quota_stockage: 10
    }
  });

  const selectedInstanceTypeId = watch('instance_type_id');
  const selectedInstanceType = instanceTypes.find(it => it.id === selectedInstanceTypeId);

  useEffect(() => {
    loadProjects();
    loadInstanceTypes();
    loadRegions();
  }, [currentOrganisation?.id, selectedRegion?.id]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await apiService.getProjects(currentOrganisation?.id, selectedRegion?.id);
      setProjects(projectsData);
    } catch (error: unknown) {
      Logger.error('Failed to load projects');
      toast.error(ErrorMessages.PROJECT_LOAD_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const loadInstanceTypes = async () => {
    try {
      const data = await apiService.getInstanceTypes();
      const mappedTypes: InstanceType[] = data.map(item => ({
        id: item.id,
        name: item.name,
        slug: item.slug || item.name.toLowerCase().replace(/\s+/g, '-'),
        cpu: item.cpu,
        ram_gb: item.ram_gb ?? item.ram ?? 0,
        description: item.description,
        active: item.active ?? true
      }));
      setInstanceTypes(mappedTypes);
    } catch (error: unknown) {
      Logger.error('Failed to load instance types', error);
      toast.error(ErrorMessages.PROJECT_LOAD_FAILED);
    }
  };

  const loadRegions = async () => {
    try {
      const data = await apiService.getRegions(currentOrganisation?.id);
      setRegions(data);
      // Set default region in form
      const defaultRegion = data.find(r => r.is_default);
      if (defaultRegion) {
        setValue('region_id', defaultRegion.id);
      }
    } catch (error: unknown) {
      Logger.error('Failed to load regions', error);
    }
  };

  // Load environments + deployments for a project (if not already cached)
  const loadProjectEnvData = async (projectId: string) => {
    if (projectEnvironments[projectId]) return;
    setLoadingEnvs(projectId);
    try {
      const envs = await apiService.getProjectEnvironments(projectId);
      setProjectEnvironments(prev => ({ ...prev, [projectId]: envs }));

      // Load deployments for each environment
      const allDeployments: Record<string, Deployment[]> = {};
      await Promise.all(
        envs.map(async (env) => {
          try {
            const deployments = await apiService.getEnvironmentDeployments(env.id);
            allDeployments[env.id] = deployments;
          } catch {
            allDeployments[env.id] = [];
          }
        })
      );
      setProjectDeployments(prev => ({ ...prev, ...allDeployments }));
    } catch {
      toast.error(t('loadError'));
      setProjectEnvironments(prev => ({ ...prev, [projectId]: [] }));
    } finally {
      setLoadingEnvs(null);
    }
  };

  // React to expandedProjectId changes (from sidebar or from this page)
  useEffect(() => {
    if (expandedProjectId) {
      loadProjectEnvData(expandedProjectId);
    }
  }, [expandedProjectId]);

  const toggleExpandProject = (projectId: string) => {
    if (expandedProjectId === projectId) {
      setExpandedProjectId(null);
    } else {
      setExpandedProjectId(projectId);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'RUNNING':
      case 'DEPLOYED':
      case 'HEALTHY':
        return 'bg-green-500';
      case 'FAILED':
        return 'bg-red-500';
      case 'PENDING':
      case 'BUILDING':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'RUNNING':
      case 'DEPLOYED':
      case 'HEALTHY':
        return 'Running';
      case 'FAILED':
        return 'Failed';
      case 'PENDING':
      case 'BUILDING':
        return 'Pending';
      default:
        return status;
    }
  };

  const getEnvTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'prod':
        return 'bg-red-500/12 text-red-400 border-red-500/30';
      case 'staging':
        return 'bg-yellow-500/12 text-yellow-400 border-yellow-500/30';
      case 'preproduction':
        return 'bg-orange-500/12 text-orange-400 border-orange-500/30';
      case 'dev':
      default:
        return 'bg-blue-500/12 text-blue-400 border-blue-500/30';
    }
  };

  const onSubmit = async (data: ProjectForm) => {
    if (!currentOrganisation) {
      toast.error(ErrorMessages.ORG_SELECTION_REQUIRED);
      return;
    }

    setCreating(true);
    try {
      if (data.resource_mode === 'PRESET') {
        if (!data.instance_type_id) {
          toast.error(ErrorMessages.PROJECT_INSTANCE_TYPE_REQUIRED);
          setCreating(false);
          return;
        }
      } else {
        if (!data.quota_cpu || !data.quota_ram) {
          toast.error(ErrorMessages.PROJECT_RESOURCES_REQUIRED);
          setCreating(false);
          return;
        }
      }

      const projectData: ProjectCreate & { organisation_id: string } = {
        nom: data.nom,
        description: data.description || null,
        resource_mode: data.resource_mode,
        quota_stockage: data.quota_stockage,
        organisation_id: currentOrganisation.id,
        region_id: data.region_id || undefined,
      };

      if (data.resource_mode === 'PRESET') {
        projectData.instance_type_id = data.instance_type_id;
      } else {
        projectData.quota_cpu = data.quota_cpu;
        projectData.quota_ram = data.quota_ram;
      }

      await apiService.createProject(projectData);
      toast.success(SuccessMessages.PROJECT_CREATED);

      await loadProjects();
      await refreshQuotaUsage();

      reset({
        resource_mode: 'PRESET',
        quota_stockage: 10
      });
      setResourceMode('PRESET');
      setShowCreateForm(false);
    } catch (error: unknown) {
      Logger.error('Failed to create project');
      toast.error(getApiErrorMessage(error));
    } finally {
      setCreating(false);
    }
  };

  if (!currentOrganisation) {
    return (
      <>
        <div className="text-center py-12">
          <BuildingOfficeIcon className="mx-auto h-12 w-12 text-text-quaternary" />
          <h3 className="mt-2 text-sm font-medium text-text-primary">{t('noOrganisation.title')}</h3>
          <p className="mt-1 text-sm text-text-tertiary">
            {t('noOrganisation.description')}
          </p>
          <div className="mt-6">
            <Link to="/organisations">
              <Button variant="primary" size="md">
                {t('noOrganisation.viewOrganisations')}
              </Button>
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Organization Context */}
      {currentOrganisation && (
        <div className="mb-6">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <div className="flex items-center">
                  <BuildingOfficeIcon className="h-5 w-5 text-text-quaternary mr-2" />
                  <Link
                    to={`/organisations/${currentOrganisation.id}/dashboard`}
                    className="text-sm font-medium text-text-tertiary hover:text-text-secondary"
                  >
                    {currentOrganisation.nom}
                  </Link>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="h-5 w-5 text-text-quaternary" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className="ml-2 text-sm font-medium text-text-secondary">
                    {t('title')}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      )}

      {/* Header */}
      <div className="lg:flex lg:items-center lg:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-text-primary sm:text-3xl sm:truncate">
            {t('title')}
          </h2>
          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              <ServerIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-text-quaternary" />
              {t('projectCount', { count: projects.length })}
            </div>
          </div>
        </div>
        <div className="mt-5 flex lg:mt-0 lg:ml-4">
          <Button
            variant="primary"
            size="md"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            {t('newProject')}
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mt-8 bg-surface-2 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary">
              {t('createProject')}
            </h3>
            <div className="mt-5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                  {/* Nom */}
                  <div className="sm:col-span-2">
                    <label htmlFor="nom" className="block text-sm font-medium text-text-secondary">
                      {t('projectName')}
                    </label>
                    <input
                      {...register('nom', { required: t('form.nameRequired') })}
                      type="text"
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="Mon projet"
                    />
                    {errors.nom && (
                      <p className="mt-1 text-sm text-red-600">{errors.nom.message}</p>
                    )}
                  </div>

                  {/* Description */}
                  <div className="sm:col-span-2">
                    <label htmlFor="description" className="block text-sm font-medium text-text-secondary">
                      Description
                    </label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder={t('projectDescription')}
                    />
                  </div>

                  {/* Region Selection */}
                  {regions.length > 0 && (
                    <div className="sm:col-span-2">
                      <label htmlFor="region_id" className="block text-sm font-medium text-text-secondary">
                        {t('region.label')}
                      </label>
                      <select
                        {...register('region_id')}
                        className="mt-1 block w-full border-border-1 bg-surface-3 text-text-primary rounded-md shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      >
                        {/* Global regions */}
                        {regions.filter(r => r.type === 'global').length > 0 && (
                          <optgroup label={t('region.nubicloud')}>
                            {regions.filter(r => r.type === 'global').map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} ({r.slug}){r.is_default ? ` — ${t('region.default')}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {/* Custom regions (only connected or configured) */}
                        {regions.filter(r => r.type === 'custom' && (r.connection_status === 'connected' || r.is_configured)).length > 0 && (
                          <optgroup label={t('region.custom')}>
                            {regions.filter(r => r.type === 'custom' && (r.connection_status === 'connected' || r.is_configured)).map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.name} ({r.slug})
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </select>
                    </div>
                  )}

                  {/* Resource Mode Selection */}
                  <div className="sm:col-span-2">
                    <label className="block text-lg font-semibold text-text-primary mb-4">
                      {t('resourceMode.title')}
                    </label>
                    <div className="flex gap-6">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="PRESET"
                          checked={resourceMode === 'PRESET'}
                          onChange={() => {
                            setResourceMode('PRESET');
                            setValue('resource_mode', 'PRESET');
                          }}
                          className="h-5 w-5 text-accent-600 focus:ring-accent-500 border-border-1"
                        />
                        <span className="ml-3 text-base font-medium text-text-secondary">
                          {t('resourceMode.preset')}
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="CUSTOM"
                          checked={resourceMode === 'CUSTOM'}
                          onChange={() => {
                            setResourceMode('CUSTOM');
                            setValue('resource_mode', 'CUSTOM');
                          }}
                          className="h-5 w-5 text-accent-600 focus:ring-accent-500 border-border-1"
                        />
                        <span className="ml-3 text-base font-medium text-text-secondary">
                          {t('resourceMode.custom')}
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* PRESET Mode - Instance Type Selection */}
                  {resourceMode === 'PRESET' && (
                    <div className="sm:col-span-2">
                      <label htmlFor="instance_type_id" className="block text-sm font-medium text-text-secondary">
                        {t('instanceType.label')}
                      </label>
                      <select
                        {...register('instance_type_id')}
                        className="mt-1 block w-full border-border-1 bg-surface-3 text-text-primary rounded-md shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      >
                        <option value="">{t('instanceType.placeholder')}</option>
                        {instanceTypes.map((it) => (
                          <option key={it.id} value={it.id}>
                            {it.name} ({it.cpu} CPU, {it.ram_gb}GB RAM)
                          </option>
                        ))}
                      </select>
                      {selectedInstanceType && (
                        <div className="mt-3 p-3 bg-accent-500/12 rounded-md">
                          <p className="text-sm text-text-secondary">
                            <strong>{t('instanceType.selected')}:</strong> {selectedInstanceType.name}
                          </p>
                          <p className="text-sm text-text-secondary">
                            CPU: {selectedInstanceType.cpu} cores | RAM: {selectedInstanceType.ram_gb} GB
                          </p>
                          <p className="text-xs text-text-tertiary mt-1">
                            {selectedInstanceType.description}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* CUSTOM Mode - Manual CPU/RAM */}
                  {resourceMode === 'CUSTOM' && (
                    <>
                      <div>
                        <label htmlFor="quota_cpu" className="block text-sm font-medium text-text-secondary">
                          CPU (cores)
                        </label>
                        <input
                          {...register('quota_cpu', {
                            required: resourceMode === 'CUSTOM' ? t('form.cpuRequired') : false,
                            min: { value: 0.1, message: t('form.cpuMin') }
                          })}
                          type="number"
                          step="0.25"
                          className="mt-1 block w-full border-border-1 bg-surface-3 text-text-primary rounded-md shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                          placeholder="1.0"
                        />
                        {errors.quota_cpu && (
                          <p className="mt-1 text-sm text-red-600">{errors.quota_cpu.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="quota_ram" className="block text-sm font-medium text-text-secondary">
                          RAM (GB)
                        </label>
                        <input
                          {...register('quota_ram', {
                            required: resourceMode === 'CUSTOM' ? t('form.ramRequired') : false,
                            min: { value: 0.5, message: t('form.ramMin') }
                          })}
                          type="number"
                          step="0.5"
                          className="mt-1 block w-full border-border-1 bg-surface-3 text-text-primary rounded-md shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                          placeholder="2.0"
                        />
                        {errors.quota_ram && (
                          <p className="mt-1 text-sm text-red-600">{errors.quota_ram.message}</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Quota Stockage */}
                  <div className="sm:col-span-2">
                    <label htmlFor="quota_stockage" className="block text-sm font-medium text-text-secondary">
                      Stockage (GB)
                    </label>
                    <input
                      {...register('quota_stockage', {
                        required: t('form.storageRequired'),
                        min: { value: 1, message: t('form.storageMin') }
                      })}
                      type="number"
                      step="1"
                      className="mt-1 block w-full border-border-1 bg-surface-3 text-text-primary rounded-md shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                      placeholder="10"
                    />
                    {errors.quota_stockage && (
                      <p className="mt-1 text-sm text-red-600">{errors.quota_stockage.message}</p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setShowCreateForm(false)}
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={creating}
                  >
                    {creating ? t('form.creating') : t('form.createProject')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Projects List */}
      <div className="mt-8" aria-live="polite">
        {loading ? (
          <div className="text-center py-12" role="status" aria-busy="true">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-text-secondary">{t('loadingProjects')}</p>
          </div>
        ) : projects.length === 0 ? (
          <div className="text-center py-12">
            <ServerIcon className="mx-auto h-12 w-12 text-text-quaternary" />
            <h3 className="mt-2 text-sm font-medium text-text-primary">{t('noProjects')}</h3>
            <p className="mt-1 text-sm text-text-tertiary">{t('noProjectsHint')}</p>
            <div className="mt-6">
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowCreateForm(true)}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('newProject')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {projects.map((project) => {
              const isExpanded = expandedProjectId === project.id;
              const envs = projectEnvironments[project.id] || [];
              const isLoadingThis = loadingEnvs === project.id;

              return (
                <div key={project.id} className="bg-surface-2 rounded-card shadow-card border border-border-1 overflow-hidden">
                  {/* Collapsed Row */}
                  <div
                    className="px-5 py-4 flex items-center cursor-pointer hover:bg-surface-0 transition-colors"
                    onClick={() => toggleExpandProject(project.id)}
                  >
                    {/* Expand icon */}
                    <div className="flex-shrink-0 mr-3 text-text-quaternary">
                      {isExpanded ? (
                        <ChevronDownIcon className="h-5 w-5" />
                      ) : (
                        <ChevronRightIcon className="h-5 w-5" />
                      )}
                    </div>

                    {/* Folder icon */}
                    <FolderIcon className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />

                    {/* Project name + badges */}
                    <div className="min-w-0 flex items-center gap-2 mr-4">
                      <span className="text-base font-bold text-text-primary truncate">
                        {project.nom}
                      </span>
                      <ProjectAccessBadge accessContext={project.access_context} />
                      {project.region && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/12 text-purple-400 border border-purple-500/30">
                          {project.region.slug}
                        </span>
                      )}
                    </div>

                    {/* Status dot */}
                    <div className="flex items-center gap-1.5 mr-6 flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-xs text-text-tertiary">{t('active')}</span>
                    </div>

                    {/* Resources */}
                    <div className="hidden md:flex items-center gap-4 text-xs text-text-tertiary mr-4 flex-shrink-0">
                      <span className="flex items-center gap-1">
                        <CpuChipIcon className="h-3.5 w-3.5 text-accent-500" />
                        {project.quota_cpu} vCPU
                      </span>
                      <span className="flex items-center gap-1">
                        <CircleStackIcon className="h-3.5 w-3.5 text-green-500" />
                        {project.quota_ram} GB RAM
                      </span>
                      <span className="flex items-center gap-1">
                        <ServerIcon className="h-3.5 w-3.5 text-purple-500" />
                        {project.quota_stockage} GB
                      </span>
                    </div>

                    {/* Right side: action buttons + env count */}
                    <div className="ml-auto flex items-center gap-2 flex-shrink-0">
                      <Link
                        to={`/projects/${project.id}`}
                        onClick={(e) => e.stopPropagation()}
                        className="p-2 text-text-tertiary hover:text-text-primary hover:bg-surface-3 rounded-lg transition-colors"
                        title={t('projectDetails')}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </Link>
                      <span className="text-xs text-text-tertiary bg-surface-3 px-2 py-1 rounded-full">
                        {isExpanded ? `${envs.length} env` : ''}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-border-1 px-5 py-4 bg-surface-0">
                      {isLoadingThis ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent-500 mr-3"></div>
                          <span className="text-text-tertiary text-sm">{t('loadingEnvironments')}</span>
                        </div>
                      ) : envs.length === 0 ? (
                        <div className="py-6 text-center">
                          <p className="text-sm text-text-tertiary mb-3">{t('noEnvironmentsInProject')}</p>
                          <Link
                            to={`/environments/${project.id}`}
                            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                          >
                            <PlusIcon className="w-4 h-4 mr-1.5" />
                            {t('environments.newEnvironment')}
                          </Link>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {envs.map((env) => {
                            const deployments = projectDeployments[env.id] || [];
                            return (
                              <div key={env.id} className="bg-surface-2 rounded-lg border border-border-1 p-4">
                                {/* Environment Header */}
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-sm font-semibold text-text-primary">{env.nom}</h4>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getEnvTypeBadgeColor(env.type)}`}>
                                      {env.type}
                                    </span>
                                    <span className="text-xs text-text-tertiary">
                                      {deployments.length} app{deployments.length !== 1 ? 's' : ''}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Link
                                      to={`/deploy?environmentId=${env.id}`}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      <PlusIcon className="w-3.5 h-3.5 mr-1" />
                                      App
                                    </Link>
                                    <Link
                                      to={`/deployments/${env.id}`}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-text-secondary bg-surface-3 rounded-lg hover:bg-surface-4 transition-colors"
                                    >
                                      {t('view')}
                                    </Link>
                                  </div>
                                </div>

                                {/* App Cards Grid */}
                                {deployments.length > 0 && (
                                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {deployments.map((deployment) => (
                                      <Link
                                        key={deployment.id}
                                        to={`/deployments/${deployment.id}/manage`}
                                        className="bg-surface-0 rounded-lg border border-border-1 p-3 hover:shadow-sm hover:border-accent-500/30 transition-all block"
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-xs font-medium text-text-tertiary uppercase">
                                            {deployment.type_application}
                                          </span>
                                          <div className="flex items-center gap-1.5">
                                            <div className={`w-2 h-2 rounded-full ${getStatusColor(deployment.statut_deploiement)}`}></div>
                                            <span className="text-xs text-text-tertiary">{getStatusLabel(deployment.statut_deploiement)}</span>
                                          </div>
                                        </div>
                                        <p className="text-sm font-medium text-text-primary truncate">{deployment.nom}</p>
                                        <div className="mt-2 flex items-center gap-3 text-xs text-text-tertiary">
                                          <span>{deployment.cpu_requis} CPU</span>
                                          <span>{deployment.ram_requise} GB</span>
                                          <span>{deployment.replicas} replica{deployment.replicas > 1 ? 's' : ''}</span>
                                        </div>
                                        {deployment.application_url && (
                                          <span
                                            className="mt-2 inline-flex items-center text-xs text-accent-400 hover:underline truncate max-w-full"
                                          >
                                            {deployment.application_url.replace(/^https?:\/\//, '')}
                                          </span>
                                        )}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}

                          {/* Bottom actions */}
                          <div className="flex items-center gap-3 pt-2">
                            <Link
                              to={`/environments/${project.id}`}
                              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-accent-600 rounded-lg hover:bg-accent-700 transition-colors"
                            >
                              <PlusIcon className="w-4 h-4 mr-1.5" />
                              {t('environments.newEnvironment')}
                            </Link>
                            <Link
                              to={`/deploy?projectId=${project.id}`}
                              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.58-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                              </svg>
                              {t('deployApp')}
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default ProjectsPage;
